import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { useLang } from "../../i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const row = {
  hidden: { opacity: 0, y: 16 },
  show: (i) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.06 } }),
};

export const UpdatesFeed = () => {
  const { t, lang } = useLang();
  const U = t.updates;
  const [remote, setRemote] = useState(null);

  useEffect(() => {
    let alive = true;
    axios
      .get(`${API}/updates`)
      .then((res) => { if (alive) setRemote(res.data || []); })
      .catch(() => { if (alive) setRemote(null); });
    return () => { alive = false; };
  }, []);

  // Prefer admin-managed updates; fall back to static i18n copy if the API is
  // unreachable so the section never renders empty.
  const items =
    remote && remote.length
      ? remote.map((u) => {
          const tr = u.translations?.[lang] || u.translations?.en || {};
          return { date: u.date, tag: tr.tag || "", text: tr.text || "" };
        })
      : U.items;

  return (
    <section id="updates" className="relative py-24 sm:py-32 bg-[var(--page)] text-[var(--ink)] border-t border-[var(--hairline)]" data-testid="updates-section">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8 grid lg:grid-cols-12 gap-12 lg:gap-16">
        <div className="lg:col-span-4">
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-10 bg-[var(--ink)]" />
            <span className="text-[11px] sm:text-xs uppercase tracking-[0.25em] text-[var(--muted-ink)] font-semibold">{U.overline}</span>
          </div>
          <h2 className="font-display font-semibold tracking-tight leading-[1.03] text-3xl sm:text-4xl">{U.title}</h2>
          <p className="mt-5 text-base text-[var(--muted-ink)] leading-relaxed">{U.sub}</p>
        </div>

        <div className="lg:col-span-8">
          <div className="border-t border-[var(--hairline)]">
            {items.map((it, i) => (
              <motion.div
                key={i} custom={i} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-40px" }} variants={row}
                data-testid={`update-item-${i}`}
                className="grid grid-cols-12 gap-4 items-baseline py-5 border-b border-[var(--hairline)]"
              >
                <div className="col-span-4 sm:col-span-2">
                  <span className="text-sm font-semibold tabular-nums text-[var(--muted-ink)]">{it.date}</span>
                </div>
                <div className="col-span-8 sm:col-span-2">
                  <span className="inline-block border border-[var(--hairline)] rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-wide">{it.tag}</span>
                </div>
                <div className="col-span-12 sm:col-span-8">
                  <p className="text-base leading-relaxed">{it.text}</p>
                </div>
              </motion.div>
            ))}
          </div>
          <p className="mt-6 text-xs text-[var(--muted-ink)] italic">{U.note}</p>
        </div>
      </div>
    </section>
  );
};
